/* assets/lab-localization.js — language and market switching as a crossfade
   (DIRECTION.md §3.4, §6 interaction 14). Defines <localization-form> in place of Dawn's
   file, which is never loaded by layout/lab.liquid (it throws without .menu-drawer).

   The kept {% form 'localization' %} holds plain lists of submit buttons named
   locale_code or country_code. One switch must cost one document load, so the POST is
   sent with `redirect: 'manual'`: Shopify's Set-Cookie is applied, its redirect is not
   followed and no HTML is downloaded, and the element then assigns the URL it already
   knows — the language's own prefix for a locale (data-root-url in the markup), the
   current URL for a country, which only lives in the session. sessionStorage.lab_switch
   is set just before that navigation; layout/lab.liquid reads it before first paint and
   adds html.lab-switch, so the cross-document transition is a plain crossfade.

   A refused POST (the theme dev server answers 401) still changes the language, because
   the locale lives in the URL prefix as well as in the session. Without JavaScript the
   browser submits the form itself, which is the platform's own path. Under
   prefers-reduced-motion: reduce, or without View Transitions, the flag is not set and
   the navigation is plain. */

if (!customElements.get('localization-form')) {
  const SWITCH_KEY = 'lab_switch';

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.querySelector('form');
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      const supportsPopover = Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover') && CSS.supports('selector(:popover-open)');
      this.querySelectorAll('[popovertarget]').forEach((button) => {
        const popover = document.getElementById(button.getAttribute('popovertarget'));
        if (!popover) return;
        if (supportsPopover) {
          popover.addEventListener(
            'toggle',
            (event) => button.setAttribute('aria-expanded', String(event.newState === 'open')),
            { signal }
          );
        } else {
          this.fallbackPopover(button, popover, signal);
        }
      });
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    // Browsers without the popover API: the button toggles [open] (CSS shows the list as a
    // bottom sheet); Escape or a click outside closes it, as the native popover would.
    fallbackPopover(button, popover, signal) {
      // a build with the attribute but without :popover-open (early Chromium 113) would still
      // toggle and hide [popover] natively: drop both so only [open] decides
      button.removeAttribute('popovertarget');
      popover.removeAttribute('popover');
      const setOpen = (open) => {
        popover.toggleAttribute('open', open);
        button.setAttribute('aria-expanded', String(open));
      };
      button.addEventListener('click', () => setOpen(!popover.hasAttribute('open')), { signal });
      document.addEventListener(
        'click',
        (event) => {
          if (popover.hasAttribute('open') && !popover.contains(event.target) && !button.contains(event.target)) setOpen(false);
        },
        { signal }
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && popover.hasAttribute('open')) {
            setOpen(false);
            button.focus();
          }
        },
        { signal }
      );
    }

    onSubmit(event) {
      const submitter = event.submitter;
      if (!(submitter instanceof HTMLButtonElement) || !submitter.name) return;
      event.preventDefault();
      submitter.setAttribute('aria-busy', 'true');

      const target = this.targetUrl(submitter);
      const body = new FormData(this.form);
      body.append(submitter.name, submitter.value);

      fetch(this.form.action, {
        method: 'POST',
        body,
        redirect: 'manual',
        credentials: 'same-origin',
        signal: this.controller.signal,
      })
        .catch((error) => {
          if (error.name === 'AbortError') throw error;
          // the cookie was refused; the prefix in the URL still carries the language
          console.warn('Localization request failed', error);
        })
        .then(() => {
          this.flagSwitch();
          window.location.assign(target);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') throw error;
          // the element left the document mid-flight: no navigation
        });
    }

    /* Where the chosen value already lives: a language in the URL prefix, a country only
       in the session, so its page is the page we are on. */
    targetUrl(submitter) {
      const root = submitter.dataset.rootUrl;
      if (!root) return window.location.href;
      const chosen = root === '/' ? '' : root.replace(/\/$/, '');
      const prefixes = Array.from(this.querySelectorAll('[data-root-url]'))
        .map((button) => button.dataset.rootUrl)
        .filter((value) => value && value !== '/')
        .map((value) => value.replace(/\/$/, ''));
      let path = window.location.pathname;
      for (const prefix of prefixes) {
        if (path === prefix || path.startsWith(`${prefix}/`)) {
          path = path.slice(prefix.length) || '/';
          break;
        }
      }
      return `${chosen}${path}${window.location.search}${window.location.hash}`;
    }

    flagSwitch() {
      if (typeof document.startViewTransition !== 'function') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      try {
        window.sessionStorage.setItem(SWITCH_KEY, '1');
      } catch (storageError) {
        // no storage (private mode, blocked cookies): the next page plays the default
        // transition, which is the same navigation with a different animation
      }
    }
  }

  customElements.define('localization-form', LocalizationForm);
}
